import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize";
        width: 100%;
        max-width: 1024px;
        height: 100%;
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto calc(35vh + 10px) minmax(calc(35vh + 10px), 1fr) auto;
        grid-column-gap: 0px;
        grid-row-gap: 0px;
    }

    .title {
        text-align: center;
        margin-top: 10px;
        overflow: auto;
    }

    .header {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        margin-top: 10px;
    }

    .header img {
        width: auto;
        height: 100%;
        image-rendering: pixelated;
        cursor: pointer;
    }

    .header img:nth-child(n + 2) {
        position: absolute;
    }

    .content {
        flex-grow: 1;
        overflow: auto;
        margin-top: 10px;
        padding: 0 10px;
    }

    .content p {
        margin: 0 0 10px 0;
    }

    .content p:last-of-type {
        margin: 0;
    }

    .footer {
        border-radius: 10px 10px 0 0;
        background-color: #52478b;
        border: 1px solid #332c57;
        margin-top: 10px;
        display: flex;
        flex-direction: column;
        height: 180px;
        overflow: auto;
        position: relative;
    }

    .footer .buttons {
        display: flex;
        flex-direction: column;
        height: auto;
        padding: 10px 10px 0 10px;
        z-index: 1;
    }

    .footer .button {
        background-color: #7f6ed7;
        border: 1px solid #332c57;
        padding: 5px 10px;
        margin: 0 0 0 10px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
        clip-path: polygon(
    0px calc(100% - 4px),
    4px calc(100% - 4px),
    4px 100%,
    calc(100% - 4px) 100%,
    calc(100% - 4px) calc(100% - 4px),
    100% calc(100% - 4px),
    100% 4px,
    calc(100% - 4px) 4px,
    calc(100% - 4px) 0px,
    4px 0px,
    4px 4px,
    0px 4px
  );
    }

    .footer .button.active,
    .footer .button:hover {
        background-color: #332c57;
    }

    .ui {
        display: flex;
        justify-content: space-between;
    }

    .hotbar {
        display: flex;
        gap: 15px;
        margin: 10px;
        padding: 10px

    }

    .item-card {
        background-color: #322535;
        border: 4px solid #332c57;
        width: 56px;
        height: 56px;
        cursor: pointer;

        border-radius: 10px;
        padding: 10px;

        clip-path: polygon(
    0px calc(100% - 16px),
    4px calc(100% - 16px),
    4px calc(100% - 8px),
    8px calc(100% - 8px),
    8px calc(100% - 4px),
    16px calc(100% - 4px),
    16px 100%,
    calc(100% - 16px) 100%,
    calc(100% - 16px) calc(100% - 4px),
    calc(100% - 8px) calc(100% - 4px),
    calc(100% - 8px) calc(100% - 8px),
    calc(100% - 4px) calc(100% - 8px),
    calc(100% - 4px) calc(100% - 16px),
    100% calc(100% - 16px),
    100% 16px,
    calc(100% - 4px) 16px,
    calc(100% - 4px) 8px,
    calc(100% - 8px) 8px,
    calc(100% - 8px) 4px,
    calc(100% - 16px) 4px,
    calc(100% - 16px) 0px,
    16px 0px,
    16px 4px,
    8px 4px,
    8px 8px,
    4px 8px,
    4px 16px,
    0px 16px
  );
    }

    .item-card-active {
        border: 4px solid #FFFFFF;
    }

    .item-hotbar {
        width: 100%;
        height: 100%;

        margin: auto;
    }

    .disabled {
        opacity: 0.5;
        pointer-events: none
    }

    .input {
        height: auto;
        margin: auto;
    }

    .number-input {
        letter-spacing: 10px;
        padding: 10px;
        margin-left: 10px;
        width: 70px;

    }
`;

/**
 * Represents the Canvas page
 */

interface ImageProperty {
    name: string;
    image: string;
    action: string;
    x: number;
    y: number;
    size: number;
    priority: number | undefined;
    autoExecute: undefined | number;
    fileType: string;
}

export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        // Create the panic meter container
        const panicContainer: HTMLDivElement = document.createElement("div");
        panicContainer.classList.add("panic-meter-wrapper");

        const panicMeter: HTMLElement = document.createElement("panic-meter-component");
        panicMeter.id = "panic-meter-component";
        panicContainer.appendChild(panicMeter);

        // Container for the rest of the UI
        const mainContent: HTMLDivElement = document.createElement("div");
        mainContent.id = "main-content";

        if (this.shadowRoot !== null) {
            this.shadowRoot.appendChild(panicContainer);
            this.shadowRoot.appendChild(mainContent);
        }

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();
        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderTitle()}
            ${this.renderHeader()}
            ${this.renderContent()}
            ${this.renderFooter()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);

        // Add event listeners AFTER elements are rendered
        setTimeout(() => {
            const roomItemElements: NodeListOf<HTMLImageElement> = this.shadowRoot!.querySelectorAll(".item-image");

            roomItemElements.forEach(image => {
                if (image.classList[1] !== "do-nothing") {
                    image.addEventListener("click", () => this.handleClickObject(image));
                }
            });

            const roomCharacterElements: NodeListOf<HTMLImageElement> = this.shadowRoot!.querySelectorAll(".character-image");

            roomCharacterElements.forEach(image => {
                if (image.classList[1] !== "do-nothing") {
                    image.addEventListener("click", () => this.handleClickObject(image));
                }
            });
        }, 0);
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;
        const itemProperties: ImageProperty[] | undefined = this._currentGameState?.itemProperties;
        const characterProperties: ImageProperty[] | undefined = this._currentGameState?.characterProperties;
        let header: string = "";
        let headerExists: boolean = false;

        if (roomImages && roomImages.length > 0) {
            headerExists = true;
            header = `
                <div class="header" style="height: 120%; position: relative;">
                    <map-component id="map-component"></map-component>
                    <inventory-component id="inventory-component"></inventory-component>
                    ${roomImages.map(url => `<img src="/assets/img/rooms/${url}.png" />`).join("")}
            `;
        }
        header += `
                <div style="position: absolute; height: 100%; aspect-ratio: 2 / 1;">
            `;
        if (characterProperties && characterProperties.length > 0) {
            headerExists = true;
            header += `
                    ${characterProperties.map(url => `<img style="${url.priority ? `z-index: ${url.priority};` : ""} cursor: pointer; position: absolute; height: ${url.size}%; left: ${url.x}%; top: ${url.y}%;" class="character-image ${url.action} ${url.name} ${url.autoExecute}" id="${url.image}" src="/assets/img/rooms/../characters/${url.image}.${url.fileType}" />
                    `).join("")}
            `;
            for (let i: number = 0; i < characterProperties.length; i++) {
                if (characterProperties[i].autoExecute) {
                    const element: HTMLImageElement = document.createElement("img");
                    element.classList.add("nothing");
                    element.classList.add(characterProperties[i].action);
                    if (characterProperties[i].name) {
                        element.classList.add(characterProperties[i].name);
                    }
                    setTimeout(() => {
                        void this.handleClickObject(element);
                    }, characterProperties[i].autoExecute);
                }
            }
        }

        if (itemProperties && itemProperties.length > 0) {
            headerExists = true;
            header += `
                    ${itemProperties.map(url => `<img style="${url.priority ? `z-index: ${url.priority};` : ""} cursor: pointer; position: absolute; height: ${url.size}%; left: ${url.x}%; top: ${url.y}%;" class="item-image ${url.action} ${url.name}" id="${url.image}" src="/assets/img/rooms/../items/${url.image}.${url.fileType}" />`).join("")}
            `;
            for (let i: number = 0; i < itemProperties.length; i++) {
                if (itemProperties[i].autoExecute) {
                    const element: HTMLImageElement = document.createElement("img");
                    element.classList.add("nothing");
                    element.classList.add(itemProperties[i].action);
                    if (itemProperties[i].name) {
                        element.classList.add(itemProperties[i].name);
                    }
                    setTimeout(() => {
                        void this.handleClickObject(element);
                    }, itemProperties[i].autoExecute);
                }
            }
        }

        header += `
            </div>
        `;

        if (headerExists) {
            header += `
                </div>
            `;
        }

        return header ? header : "";
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        return `
            <div class="content" style="margin-top: 155px;">
                ${this._currentGameState?.text.map(text => `<p>${text}</p>`).join("") || ""}
            </div>
        `;
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        const isStartup: boolean = this._currentGameState?.roomAlias === "startup";
        if (isStartup) {
            // Return empty html as the footer while on the start screen
            return html`<p></p>`;
        }

        const inventory: string[] = this._currentGameState?.inventory || [];
        const itemCards: string[] = Array.from({ length: 2 }, (_, i) => inventory[i] || "Empty");

        const blueprintState: boolean | undefined = this._currentGameState?.blueprint;
        const pickedUpWater: boolean | undefined = this._currentGameState?.pickedUpWater;
        const drankWater: boolean | undefined = this._currentGameState?.drankWater;

        // set timeout to make changes to footer frontend after it is loaded in
        setTimeout(() => {
            if (blueprintState) {
                const mapButton: HTMLButtonElement = this.shadowRoot!.getElementById("map") as HTMLButtonElement;
                mapButton.addEventListener("click", () => {
                    this.shadowRoot!.getElementById("map-component")!.style.display = "inline";
                });
            }
            const inventoryButton: HTMLButtonElement = this.shadowRoot!.getElementById("inventory") as HTMLButtonElement;
            inventoryButton.addEventListener("click", () => {
                this.shadowRoot!.getElementById("inventory-component")!.style.display = "inline";
            });
            const drinkButton: HTMLElement | null = this.shadowRoot!.getElementById("drink-water");
            if (drinkButton) {
                drinkButton.addEventListener("click", async () => {
                    if (!drankWater) {
                        const oldPanicPoint: number = this._currentGameState?.panicPoints ?? 100;
                        let newPanicPoint: number = 0;
                        if (oldPanicPoint + 10 > 99.99) {
                            newPanicPoint = 99.99;
                            if (oldPanicPoint === 100) {
                                newPanicPoint = 100;
                            }
                        }
                        else {
                            newPanicPoint = oldPanicPoint + 10;
                        }
                        const newState: GameState | undefined = await this._gameRouteService.updatePanicPoint(newPanicPoint);
                        if (!newState) {
                            return;
                        }
                        this.updateGameState(newState);
                        if (newPanicPoint < 100) {
                            const newState2: GameState | undefined = await this._gameRouteService.changeWaterState(true);
                            if (!newState2) {
                                return;
                            }
                            this.updateGameState(newState2);
                        }
                    }
                });
            }

            if (this._currentGameState?.roomAlias === "west-hallway-codelock") {
                const inputContainer: HTMLDivElement = this.shadowRoot!.getElementById("input") as HTMLDivElement;
                inputContainer.innerHTML += "<input id='codeInput' class='number-input' maxlength='4' placeholder='0000' type='text' inputmode='numeric'>";
                this.shadowRoot!.getElementById("input")!.style.display = "inline-block";

                const inputField: HTMLInputElement = this.shadowRoot!.getElementById("codeInput") as HTMLInputElement;
                inputField.addEventListener("input", async () => {
                    if (inputField.value.length === inputField.maxLength) {
                        await this.handleCodelock(inputField);
                    }
                });

                // Prevent non number inputs in the code field.
                inputField.addEventListener("keypress", e => {
                    if (!/[0-9]/.test(e.code)) {
                        e.preventDefault();
                    }
                });
            }

            const hotbarElements: NodeListOf<HTMLDivElement> = this.shadowRoot!.querySelectorAll(".item-card");
            let hotbarIndex: number = 0;

            hotbarElements.forEach(card => {
                const cardIndex: number = hotbarIndex++;
                card.addEventListener("click", async () => {
                    const oldHeldItem: string | undefined = this._currentGameState?.hand;

                    const newState: GameState | undefined = await this.handleClickHotbar(cardIndex);
                    if (!newState) {
                        return; // Bad request
                    }

                    if (oldHeldItem && this._currentGameState?.hand !== oldHeldItem) {
                        // Remove old border styling
                        const oldItemElement: HTMLElement | null = this.shadowRoot!.getElementById(oldHeldItem);
                        if (oldItemElement) {
                            oldItemElement.style.border = "";
                        }
                    }

                    if (this._currentGameState?.hand === card.id) {
                        this.shadowRoot!.getElementById(card.id)!.style.border = "4px solid #FFFFFF";
                    }
                });
            });
        }, 0);

        return html`
            <div class="footer">
                <panic-meter-component id="panic-meter-component"></panic-meter-component>
                <div class="buttons">
                    <div>
                        <div class="input" id="input">
                        </div>
                        ${blueprintState ? "<a class='button' id='map'>Map</a>" : ""}
                        ${pickedUpWater && !drankWater ? "<a class='button' id='drink-water'>Drink</a>" : ""}
                        <a class='button' id='inventory'>Items</a>
                        ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                    </div>
                    <div>
                        ${this._selectedActionButton
                            ? this._currentGameState?.objects.map(button => this.renderGameObjectButton(button)) || ""
                            : ""
                        }
                    </div>
                </div>
                <div class="ui" id="ui">

                    <section class="hotbar">
                        ${itemCards.map(item => html`
                            <div class="item-card" id="${item}">
                                <img class="item-hotbar" src="/assets/img/items/${item}.png" />
                            </div>
                        `)}
                    </section>

                    <section class="face">

                    </section>

                    <section class="meter">
                    </section>
                </div>
            </div>
        `;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedActionButton === button ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */

    private async handleClickObject(image: HTMLImageElement): Promise<void> {
        // Add logic to handle the click
        let state: GameState | undefined;
        if (image.classList[2]) {
            state = await this._gameRouteService.executeAction(image.classList[1], [image.classList[2]]);
        }
        else {
            state = await this._gameRouteService.executeAction(image.classList[1]);
        }
        if (state === undefined) {
            return;
        }
        this.updateGameState(state);
    }

    private async handleClickAction(button: ActionReference): Promise<void> {
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.render();
            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);
        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    private async handleClickHotbar(index: number): Promise<GameState | undefined> {
        if (!this._currentGameState) {
            return;
        }

        // Prevent sending a request for out of bounds clicks, or slots with no item
        if (index < 0 || index >= this._currentGameState.inventory.length) {
            return;
        }

        const newState: GameState | undefined = await this._gameRouteService.hotbarClicked(index);
        if (!newState) {
            return;
        }

        this.updateGameState(newState);
        return newState;
    }

    private async handleCodelock(input: HTMLInputElement): Promise<GameState | undefined> {
        if (!this._currentGameState) {
            return;
        }

        const newState: GameState | undefined = await this._gameRouteService.inputFieldInput(input.value);
        if (!newState) {
            return;
        }

        this.updateGameState(newState);
        return newState;
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */

    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }
        // Otherwise, update the game state.
        this.updateGameState(state);
    }
}
